import React, {createRef} from "react";
import {connect} from "react-redux";
import NumberFormat from "react-number-format";
import {
  Button,
  Select,
  Input,
  Textarea,
  SelectActions,
  Modal,
} from "../../components";
import {Globals, axios} from "../../utils";

const taxpayerTypes = [
  {value: 1, label: "Ordinario"},
  {value: 2, label: "Formal"},
  {value: 3, label: "Ocasional"},
  {value: 4, label: "Pasivo especial"},
];

const concepts = [
  {value: 1, label: "Producto"},
  {value: 2, label: "Servicio"},
  {value: 3, label: "MIXTA (la misma factura incluye productos y servicios)"},
];

/**
 * UX layout (resumido):
 * 1) Cliente (full width)
 * 2) Factura/Orden (buscador) + Monto total factura (disabled)
 * 3) Fecha de factura (disabled)
 * 4) Tipo de contribuyente + Concepto
 * 5) Tasas (IVA, IVA retenido, ISLR)
 * 6) Descripción
 * 7) Guardar
 */
class CreateWithholding extends React.Component {
  state = {
    visible_client: false,
    created_client: null,
    // === FORM alineado con el backend createRetention ===
    form: {
      user_id: this.props.user.id,
      client_id: "",
      taxpayer_type: "",
      concept: 1,
      bill_number: "",
      control_number: "",
      bill_date: "",
      description: "",
      order_id: "",
      iva_rate: 16, // default 16%
      iva_withheld_rate: "", // 75 o 100
      islr_rate: "",
      sustraendo: "",
      status: 0, // 0=borrador; 1/2 opcional
    },

    // === UI/aux ===
    clients: [],
    orders: [],
    client_search: "",
    order_search: "",
    ui_order_total: "", // solo visual; tomado de order.total
  };

  clientDebounce = createRef();
  orderDebounce = createRef();

  componentDidMount() {
    this.loadClients();
    this.loadOrders();
  }

  // ---------- helpers ----------
  change = async (value, target) => {
    await this.setState((s) => ({form: {...s.form, [target]: value}}));
  };

  toggleClientModal = () =>
    this.setState((s) => ({visible_client: !s.visible_client}));

  // ---------- CLIENTES (mismo patrón de NewRequest) ----------
  loadClients = async () => {
    Globals.setLoading();
    try {
      const filter = {
        user_id: this.state.form.user_id,
        search: this.state.client_search,
      };
      const res = await axios.post("web/admin/orders/clients", filter);
      if (!res?.data?.clients) throw Error("No se encontraron los clientes");

      const active = (res.data.clients.data || []).filter(
        (c) => c.status === 1,
      );

      let clients = active.map((c) => ({
        ...c,
        value: c.id,
        label: c.document ? `${c.name} - Rif ${c.document}` : c.name,
      }));

      if (this.state.created_client) {
        clients = clients.filter((i) => i.id !== this.state.created_client.id);
        clients.unshift({
          ...this.state.created_client,
          value: this.state.created_client.id,
          label: this.state.created_client.document
            ? `${this.state.created_client.name} - Rif ${this.state.created_client.document}`
            : this.state.created_client.name,
        });
      }

      this.setState({clients});
    } catch (e) {
      Globals.showError(e?.message);
    }
    Globals.quitLoading();
  };

  debouncingSearchClient = async (search) => {
    if (this.clientDebounce.current) clearTimeout(this.clientDebounce.current);
    await this.change("", "client_id");
    await this.setState({client_search: search});
    this.clientDebounce.current = setTimeout(this.loadClients, 800);
  };

  onSelectClient = async (id) => {
    await this.change(id, "client_id");
  };

  // ---------- ÓRDENES (usa admin/orders/retentions/getfilters?only_orders=true) ----------
  loadOrders = async () => {
    try {
      const payload = {
        user_id: this.state.form.user_id,
        only_orders: true,
        search: this.state.order_search || undefined,
      };
      const {data} = await axios.post(
        "admin/orders/retentions/getfilters",
        payload,
      );
      if (!data?.result)
        throw Error(data?.message || "No se pudieron cargar las órdenes");
      const orders = (data.orders || []).map((o) => {
        const billNumber =
          o.order_bill?.bill_number ||
          o.bill_number ||
          o.number_format ||
          `#${o.id}`;
        return {
          ...o,
          value: o.id,
          label: billNumber,
        };
      });

      this.setState({orders});
    } catch (e) {
      Globals.showError(e?.message || "Error cargando órdenes");
    }
  };
  debouncingSearchOrder = async (search) => {
    if (this.orderDebounce.current) clearTimeout(this.orderDebounce.current);
    await this.change("", "order_id");
    await this.setState({order_search: search, ui_order_total: ""});
    this.orderDebounce.current = setTimeout(this.loadOrders, 800);
  };

  onSelectOrder = async (id) => {
    const order = this.state.orders.find((o) => o.id === id);
    await this.change(id, "order_id");

    if (order?.client?.id) {
      await this.change(order.client.id, "client_id");
    }

    // Castear explícitamente a String para evitar enviar enteros al backend
    const bill = String(
      order?.order_bill?.bill_number ||
        order?.bill_number ||
        order?.number_format ||
        "",
    );
    const control = String(
      order?.order_bill?.control_number || order?.control_number || "",
    );

    await this.change(bill, "bill_number");
    await this.change(control, "control_number");

    // Fecha de factura (disabled)
    if (order?.created_at) {
      try {
        const d = new Date(order.created_at);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        await this.change(`${yyyy}-${mm}-${dd}`, "bill_date");
      } catch (_) {}
    }

    // Monto total de la factura (solo visual)
    this.setState({ui_order_total: order?.total_bs ?? ""});
  };

  // ---------- Validación y submit ----------
  isValidForm = () => {
    const onError = (msg) => {
      Globals.showError(msg);
      return false;
    };
    const {form} = this.state;

    if (!form.client_id) return onError("Debe seleccionar el cliente");
    if (!form.taxpayer_type)
      return onError("Debe indicar el tipo de contribuyente");
    if (!form.concept) return onError("Debe indicar el concepto");
    if (!form.order_id)
      return onError("Debe seleccionar la orden (N° de factura)");
    // if (!form.description) return onError('Debe indicar la descripción');

    // Tasas
    if (
      (form.concept === 2 || form.concept === 3) &&
      (form.islr_rate === "" || Number(form.islr_rate) < 0)
    ) {
      return onError("Debe indicar la tasa de ISLR (>= 0)");
    }
    if (form.iva_rate === "" || Number(form.iva_rate) < 0) {
      return onError("Debe indicar la tasa de IVA (>= 0)");
    }
    if (form.iva_withheld_rate === "" || Number(form.iva_withheld_rate) < 0) {
      return onError(
        "Debe indicar el porcentaje de IVA a retener (75% o 100%)",
      );
    }

    return true;
  };

  submit = async () => {
    if (!this.isValidForm()) return;
    try {
      Globals.setLoading();
      const payload = {...this.state.form};
      const res = await axios.post("admin/orders/retentions/create", payload);

      if (res?.data?.result) {
        Globals.showSuccess("Retención creada correctamente");
        this.resetForm();

        // Cerrar modal
        if (typeof this.props.onClose === "function") {
          this.props.onClose();
        }
      } else {
        Globals.showError(
          res?.data?.message || "No se pudo crear la retención",
        );
      }
    } catch (e) {
      Globals.showError(e?.message || "Error al crear la retención");
    }
    Globals.quitLoading();
  };

  resetForm = () => {
    this.setState({
      form: {
        user_id: this.props.user.id,
        client_id: "",
        taxpayer_type: "",
        concept: 1,
        bill_number: "",
        bill_date: "",
        description: "",
        order_id: "",
        iva_rate: 16,
        iva_withheld_rate: "",
        islr_rate: "",
        sustraendo: "",
        status: 0,
      },
      ui_order_total: "",
    });
  };

  render() {
    const {form, clients, orders, visible_client, ui_order_total} = this.state;

    return (
      <>
        {/* Modal opcional para crear cliente */}
        <Modal
          size="lg"
          className="modal-report-sale-client"
          title="Registrar cliente"
          visible={visible_client}
          onClose={this.toggleClientModal}
        >
          {/* Inserta aquí tu <CreateEditClient /> si lo usas en este módulo */}
          <div style={{padding: 12}}>Próximamente: formulario de cliente</div>
        </Modal>

        <div className="modal-create-withholding">
          <div className="row g-3">
            <div className="col-12">
              <h6
                className="text-muted fw-bold mb-0"
                style={{
                  borderBottom: "1px solid #e0e0e0",
                  paddingBottom: "0.5rem",
                }}
              >
                Información del Cliente
              </h6>
            </div>
            {/* 1) Cliente */}
            <div className="col-12">
              <SelectActions
                label="Nombre del cliente"
                placeholder="Nombre del Cliente o Rif"
                isSearchable
                actionOptions={[
                  {
                    onClick: () => this.toggleClientModal(),
                    content: (
                      <div style={{color: "#3A7FC2"}}>
                        <span>Registrar nuevo cliente</span>
                        <i
                          className="fa fa-regular fa-plus"
                          style={{marginLeft: "0.75rem"}}
                        />
                      </div>
                    ),
                  },
                ]}
                options={clients.map((i) => ({
                  value: i.id,
                  label: i.label,
                  actions: [],
                }))}
                value={form.client_id}
                onChange={this.onSelectClient}
                onInputChange={this.debouncingSearchClient}
              />
            </div>

            <div className="col-12 mt-3">
              <h6
                className="text-muted fw-bold mb-0"
                style={{
                  borderBottom: "1px solid #e0e0e0",
                  paddingBottom: "0.5rem",
                }}
              >
                Datos de la Factura
              </h6>
            </div>

            {/* 2) Factura + Total (lado a lado) */}
            <div className="col-12 col-lg-6">
              <SelectActions
                label="N° de factura / Orden"
                placeholder="Escribe para buscar"
                isSearchable
                options={orders.map((o) => ({
                  value: o.id,
                  label: o.label,
                  actions: [],
                }))}
                value={form.order_id}
                onChange={this.onSelectOrder}
                onInputChange={this.debouncingSearchOrder}
              />
            </div>
            <div className="col-12 col-lg-6">
              <label className="material">Monto total factura</label>
              <NumberFormat
                value={ui_order_total}
                className="form-control material"
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                disabled
              />
            </div>

            {/* 3) Fecha de factura (disabled) */}
            <div className="col-12 col-md-6">
              <Input
                type="date"
                color=" "
                label="Fecha de factura"
                className="filter"
                value={form.bill_date}
                disabled
              />
            </div>

            {/* 4) Tipo de contribuyente + Concepto */}
            <div className="col-12 col-md-6">
              <Select
                color="white"
                label="Tipo de contribuyente"
                defaultname="Seleccione"
                className="filter"
                disabledFirst="false"
                value={form.taxpayer_type}
                onChange={(e) =>
                  this.change(Number(e.target.value), "taxpayer_type")
                }
                options={taxpayerTypes}
              />
            </div>

            <div className="col-12 mt-3">
              <h6
                className="text-muted fw-bold mb-0"
                style={{
                  borderBottom: "1px solid #e0e0e0",
                  paddingBottom: "0.5rem",
                }}
              >
                Concepto y Tasas
              </h6>
            </div>

            <div className="col-12">
              <label>Concepto de retención</label>
            </div>
            <div className="col-12 mb-1 d-flex flex-wrap">
              {concepts.map((item, index) => {
                const isActive = form.concept === item.value;
                return (
                  <div
                    key={index}
                    className="checkbox me-4 mb-2"
                    onClick={() => this.change(item.value, "concept")}
                  >
                    <div className={"check" + (isActive ? " checked" : "")} />
                    <label
                      style={{
                        cursor: "pointer",
                        fontWeight: isActive ? "500" : "normal",
                      }}
                    >
                      {item.label}
                    </label>
                  </div>
                );
              })}
            </div>

            {/* 5) Tasas */}
            <div className="col-md-6">
              <NumberFormat
                value={form.iva_rate}
                className="form-control material"
                allowNegative={false}
                decimalScale={2}
                onValueChange={(v) => this.change(v.value, "iva_rate")}
              />
              <label className="material" style={{marginTop: 4}}>
                IVA (%)
              </label>
            </div>
            <div className="col-md-6">
              <NumberFormat
                value={form.iva_withheld_rate}
                className="form-control material"
                placeholder="75% o 100%"
                allowNegative={false}
                decimalScale={2}
                onValueChange={(v) => this.change(v.value, "iva_withheld_rate")}
              />
              <label className="material" style={{marginTop: 4}}>
                IVA retenido (75% - 100%)
              </label>
            </div>
            {(form.concept === 2 || form.concept === 3) && (
              <>
                <div className="col-md-6">
                  <NumberFormat
                    value={form.islr_rate}
                    className="form-control material"
                    allowNegative={false}
                    decimalScale={2}
                    onValueChange={(v) => this.change(v.value, "islr_rate")}
                  />
                  <label className="material" style={{marginTop: 4}}>
                    ISLR (%)
                  </label>
                </div>
                {form.concept !== 3 && (
                  <div className="col-md-6">
                    <NumberFormat
                      value={form.sustraendo}
                      className="form-control material"
                      allowNegative={false}
                      decimalScale={2}
                      onValueChange={(v) => this.change(v.value, "sustraendo")}
                    />
                    <label className="material" style={{marginTop: 4}}>
                      Sustraendo (Opcional)
                    </label>
                  </div>
                )}
              </>
            )}

            {/* 6) Descripción */}
            {/* <div className="col-12">
              <Textarea
                label="Descripción"
                value={form.description}
                onChange={e => this.change(e.target.value, 'description')}
              />
            </div> */}
          </div>

          {/* 7) CTA */}
          <div id="button" className="d-flex justify-content-center mt-4 mb-2">
            <Button
              color=" "
              className="primary px-5 py-2"
              type="button"
              onClick={this.submit}
            >
              <span className="fw-bold">Guardar Retención</span>
            </Button>
          </div>
        </div>
      </>
    );
  }
}

export default connect((state) => ({user: state.user}))(CreateWithholding);
